<script lang="ts" setup>
/**
 * OzTabsPaginated vs OzTabs - Key differences:
 * • Content: OzTabsPaginated requires icon-only tabs; OzTabs supports text and/or icons
 * • Layout: OzTabsPaginated shows fixed tabs per page with prev/next navigation; OzTabs shows all tabs at once
 * • Indicator: OzTabs has animated bottom indicator line; OzTabsPaginated has none
 * • Size: OzTabsPaginated uses button height presets (H18px-H44px); OzTabs uses text size presets (Small/ExtraSmall)
 * • Use case: Choose OzTabsPaginated for many icon-only tabs; OzTabs for text/mixed tabs or when showing all tabs is preferred
 */
import { dir } from '@@/bits/current_dir'
import { __ } from '@@/bits/intl'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import OzPlainButton, { OzPlainButtonColorScheme } from '@@/library/v4/components/OzPlainButton.vue'
import OzTabsBase from '@@/library/v4/components/OzTabsBase.vue'
import { useElementSize, syncRef } from '@vueuse/core'
import { computed, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    /**
     * Tabs to display.
     */
    tabs: OzTabsPaginatedButtonProps[]
    /**
     * Maximum number of tabs to display per page (including navigation buttons).
     *
     * Note: Minimum effective value is 3 (to ensure at least 1 navigation button + 2 tabs or 2 navigation buttons + 1 tab).
     * Values less than 3 will be automatically adjusted to 3 to prevent infinite loops in pagination.
     */
    maxItemsPerPage: number
    /**
     * Label corresponding to the tab list.
     */
    tabListAriaLabel: string
    /**
     * @see `OzTabsBaseAccessibilityRole`
     */
    accessibilityRole?: OzTabsPaginatedAccessibilityRole
    /**
     * Key used to detect the current tab.
     */
    currentTabKey?: string
    /**
     * Tabs size preset.
     * Same as `OzPlainButtonSizePreset`.
     */
    sizePreset?: OzTabsPaginatedSizePreset
    /**
     * @default 'auto'
     */
    darkMode?: boolean | 'auto'
    /**
     * Classes applied to the tabs container.
     */
    tabsContainerClasses?: string
  }>(),
  {
    accessibilityRole: OzTabsPaginatedAccessibilityRole.Tabs,
    currentTabKey: undefined,
    sizePreset: OzTabsPaginatedSizePreset.H40px,
    darkMode: 'auto',
    tabsContainerClasses: undefined,
  },
)

const emit = defineEmits<{
  (
    e: 'activate-tab',
    event: Event,
    tab: {
      key: string
    },
  ): void
  (name: 'mouseenter', event: MouseEvent, tab: OzTabsPaginatedButtonProps): void
  (name: 'focus', event: FocusEvent, tab: OzTabsPaginatedButtonProps): void
  (name: 'blur', event: FocusEvent, tab: OzTabsPaginatedButtonProps): void
  (name: 'mouseleave', event: MouseEvent, tab: OzTabsPaginatedButtonProps): void
}>()

const baseComponent = ref<InstanceType<typeof OzTabsBase> | null>(null)
const rootEl = computed<HTMLElement | null>(() => baseComponent.value?.$el as HTMLElement | null)

// #region Styles
const tabButtonWidth = computed(() => {
  return {
    [OzTabsPaginatedSizePreset.H18px]: 18,
    [OzTabsPaginatedSizePreset.H24px]: 24,
    [OzTabsPaginatedSizePreset.H28px]: 28,
    [OzTabsPaginatedSizePreset.H32px]: 32,
    [OzTabsPaginatedSizePreset.H40px]: 40,
    [OzTabsPaginatedSizePreset.H44px]: 44,
  }[props.sizePreset]
})

const tabButtonIconSize = computed(() => {
  return {
    [OzTabsPaginatedSizePreset.H18px]: 12,
    [OzTabsPaginatedSizePreset.H24px]: 16,
    [OzTabsPaginatedSizePreset.H28px]: 16,
    [OzTabsPaginatedSizePreset.H32px]: 20,
    [OzTabsPaginatedSizePreset.H40px]: 24,
    [OzTabsPaginatedSizePreset.H44px]: 24,
  }[props.sizePreset]
})

const { width: rootElWidth } = useElementSize(rootEl)

const effectiveMaxItemsPerPage = computed(() => {
  // Ensure minimum of 3 to prevent infinite loops in pagination
  // (1 nav button + 2 tabs, or 2 nav buttons + 1 tab)
  return Math.max(3, props.maxItemsPerPage)
})

const tabItemGap = computed(() => {
  if (effectiveMaxItemsPerPage.value <= 1) return 0
  const remainingWidth = rootElWidth.value - tabButtonWidth.value * effectiveMaxItemsPerPage.value
  // If there are `n` tabs, there are `n - 1` gaps.
  return Math.max(0, remainingWidth / (effectiveMaxItemsPerPage.value - 1))
})
// #endregion

// #region Paginate the tabs
const PREVIOUS_BUTTON_KEY = __('Previous tab')
const previousButton = computed<OzTabsPaginatedButtonProps>(() => ({
  key: PREVIOUS_BUTTON_KEY,
  ariaHidden: true,
  ariaLabel: __('Previous tabs'),
  iconName: 'chevron_left_double_thin',
  iconSize: tabButtonIconSize.value,
}))

const NEXT_BUTTON_KEY = __('Next tab')
const nextButton = computed<OzTabsPaginatedButtonProps>(() => ({
  key: NEXT_BUTTON_KEY,
  ariaHidden: true,
  ariaLabel: __('Next tabs'),
  iconName: 'chevron_right_double_thin',
  iconSize: tabButtonIconSize.value,
}))

const FILLER_TAB_KEY_PREFIX = 'filler-tab'
const createFillerTab = (index: number): OzTabsPaginatedButtonProps => ({
  key: `${FILLER_TAB_KEY_PREFIX}-${index}`,
  ariaHidden: true,
  ariaLabel: '',
  iconName: '',
  iconSize: tabButtonIconSize.value,
})

// We want to maintain consistent gaps between tab items. However, the last page likely has
// less items than the other pages -> there's more free space to distribute due to justify-between.
// -> The solution is to add filler tabs that's aria-hidden="true" only to even out the spacing.
const displayedTabsByPage = computed<OzTabsPaginatedButtonProps[][]>(() => {
  if (props.tabs.length === 0) return []

  const allTabs = props.tabs

  // If all tabs fit, add fillers to match effectiveMaxItemsPerPage
  if (allTabs.length <= effectiveMaxItemsPerPage.value) {
    const page = [...allTabs]
    const fillersNeeded = effectiveMaxItemsPerPage.value - allTabs.length
    for (let i = 0; i < fillersNeeded; i++) {
      page.push(createFillerTab(i))
    }
    return [page]
  }

  const pages: OzTabsPaginatedButtonProps[][] = []
  let remainingTabs = allTabs

  while (remainingTabs.length > 0) {
    const isFirstPage = pages.length === 0
    const isLastPage = remainingTabs.length <= effectiveMaxItemsPerPage.value - 1
    const page: OzTabsPaginatedButtonProps[] = []

    if (isFirstPage) {
      // First page: tabs + nextButton
      const tabsToAdd = allTabs.slice(0, effectiveMaxItemsPerPage.value - 1)
      page.push(...tabsToAdd)
      page.push(nextButton.value)
    } else if (isLastPage) {
      // Last page: previousButton + remaining tabs + fillers
      page.push(previousButton.value)
      page.push(...remainingTabs)

      // Add fillers to match effectiveMaxItemsPerPage
      const currentPageSize = 1 + remainingTabs.length // 1 for previousButton
      const fillersNeeded = effectiveMaxItemsPerPage.value - currentPageSize
      for (let i = 0; i < fillersNeeded; i++) {
        page.push(createFillerTab(pages.length * 1000 + i)) // Use page index to ensure unique IDs
      }
    } else {
      // Middle pages: previousButton + tabs + nextButton
      page.push(previousButton.value)
      const tabsToAdd = remainingTabs.slice(0, effectiveMaxItemsPerPage.value - 2)
      page.push(...tabsToAdd)
      page.push(nextButton.value)
    }

    pages.push(page)

    // Update remainingTabs for next iteration
    if (isFirstPage) {
      remainingTabs = allTabs.slice(effectiveMaxItemsPerPage.value - 1)
    } else if (isLastPage) {
      remainingTabs = []
    } else {
      remainingTabs = remainingTabs.slice(effectiveMaxItemsPerPage.value - 2)
    }
  }

  return pages
})

const displayedTabs = computed(() => {
  return displayedTabsByPage.value.flat()
})
// #endregion

// #region Switching between pages
function findPageContainingTab(tabKey: string): number {
  return displayedTabsByPage.value.findIndex((page) => page.some((tab) => tab.key === tabKey))
}

const currentTabPageIndex = computed(() => {
  if (!baseComponent.value) return -1
  return findPageContainingTab(baseComponent.value.currentTabId)
})

// Track the currently visible page independently of the active tab
const currentVisiblePageIndex = ref(0)

// One-directional sync: when active tab's page changes, update visible page
syncRef(currentTabPageIndex, currentVisiblePageIndex, { direction: 'ltr' })

function switchVisiblePage(pageIndex: number) {
  if (baseComponent.value?.list == null) return

  const offset = calculatePageOffset(pageIndex)

  // In RTL, scrollLeft works differently:
  // - In LTR: scrollLeft = 0 is the leftmost position
  // - In RTL: scrollLeft = 0 is the rightmost position
  // Invert the offset for RTL
  baseComponent.value.list.scrollLeft = dir() === 'rtl' ? -offset : offset
}

/**
 * Handle navigation button clicks to switch pages without activating any tab.
 */
function handleNavigationButtonClick(tab: OzTabsPaginatedButtonProps) {
  if (displayedTabsByPage.value.length === 0) return

  let targetPageIndex = -1

  if (tab.key === PREVIOUS_BUTTON_KEY) {
    targetPageIndex = Math.max(0, currentVisiblePageIndex.value - 1)
  } else if (tab.key === NEXT_BUTTON_KEY) {
    targetPageIndex = Math.min(displayedTabsByPage.value.length - 1, currentVisiblePageIndex.value + 1)
  }

  if (targetPageIndex !== -1 && targetPageIndex !== currentVisiblePageIndex.value) {
    switchVisiblePage(targetPageIndex)
    currentVisiblePageIndex.value = targetPageIndex
  }
}
// #endregion

// #region Auto-scroll when switching pages
function calculatePageOffset(pageIndex: number): number {
  let offset = 0

  // For each page before the target page, add up the widths and gaps
  for (let i = 0; i < pageIndex; i++) {
    const pageTabCount = displayedTabsByPage.value[i].length
    // Width of all tabs in this page
    offset += pageTabCount * tabButtonWidth.value
    // Gaps between tabs in this page (n tabs = n-1 gaps)
    // Add one more gap for the space between this page and the next
    // => n gaps
    offset += pageTabCount * tabItemGap.value
  }

  return offset
}

watch(
  () => baseComponent.value?.currentTabId,
  (newTabId, oldTabId) => {
    if (
      newTabId == null ||
      oldTabId == null ||
      baseComponent.value?.list == null ||
      displayedTabsByPage.value.length === 0
    )
      return

    const oldPageIndex = findPageContainingTab(oldTabId)
    const newPageIndex = findPageContainingTab(newTabId)

    // If both tabs are on the same page or we couldn't find them, no need to scroll
    if (oldPageIndex === newPageIndex || oldPageIndex === -1 || newPageIndex === -1) return

    switchVisiblePage(newPageIndex)
  },
  { immediate: true },
)
// #endregion

// #region Handle keyboard navigation scrolling
function handleKeyboardNavigation(direction: 'left' | 'right', targetTab: { key: string }) {
  if (baseComponent.value?.list == null || displayedTabsByPage.value.length === 0) return

  const targetPageIndex = findPageContainingTab(targetTab.key)
  if (targetPageIndex === -1) return

  const currentPage = displayedTabsByPage.value[targetPageIndex]
  if (currentPage == null) return

  // Find the positions of navigation buttons in the current page
  const hasPreviousButton = currentPage.some((tab) => tab.key === PREVIOUS_BUTTON_KEY)
  const hasNextButton = currentPage.some((tab) => tab.key === NEXT_BUTTON_KEY)

  // Calculate the base offset for this page
  const pageOffset = calculatePageOffset(targetPageIndex)

  // Determine scroll position based on navigation direction and buttons present
  let scrollPosition = pageOffset

  if (hasPreviousButton && hasNextButton) {
    // Middle page: ensure appropriate button is hidden based on direction
    const tabWidth = tabButtonWidth.value
    const gap = tabItemGap.value
    const isRtl = dir() === 'rtl'

    // In RTL, the logical direction is reversed:
    // - Arrow left in RTL means going forward (next)
    // - Arrow right in RTL means going backward (previous)
    const isNavigatingForward = isRtl ? direction === 'left' : direction === 'right'

    if (isNavigatingForward) {
      // Navigating forward: hide the previous button by scrolling past it
      scrollPosition = pageOffset + tabWidth + gap
    } else {
      // Navigating backward: show from beginning (which hides the next button on the right)
      scrollPosition = pageOffset
    }
  }
  // For first page (only has next button) or last page (only has previous button),
  // the pageOffset is already correct

  baseComponent.value.list.scrollTo({
    left: dir() === 'rtl' ? -scrollPosition : scrollPosition,
    behavior: 'instant',
  })
}
// #endregion

function computeTabAttributes(
  tab: OzTabsPaginatedButtonProps,
  baseAttributes: Record<string, any>,
): Record<string, any> {
  return baseComponent.value?.isUsingTabsAccessibilityRole === true
    ? {
        ...baseAttributes,
        'aria-label': tab.iconName ? tab.ariaLabel : undefined,
      }
    : baseAttributes
}
</script>

<script lang="ts">
import { OzPlainButtonSizePreset as OzTabsPaginatedSizePreset } from '@@/library/v4/components/OzPlainButton.vue'
import {
  OzTabsBaseAccessibilityRole as OzTabsPaginatedAccessibilityRole,
  type OzTabsBaseTab,
} from '@@/library/v4/components/OzTabsBase.vue'

interface OzTabsPaginatedButtonProps extends OzTabsBaseTab {
  /**
   * `aria-label` attribute.
   */
  ariaLabel: string
  /**
   * The tab button title.
   * @default `ariaLabel`
   */
  title?: string
  /**
   * The name of the icon to display on the tab.
   */
  iconName: string
  /**
   * The name of the icon to display on the tab when it's active.
   * @default `iconName`
   */
  activeIconName?: string
  /**
   * The size of the icon to display on the tab.
   */
  iconSize: 12 | 16 | 20 | 24
}

export { OzTabsPaginatedAccessibilityRole, OzTabsPaginatedSizePreset }
export type { OzTabsPaginatedButtonProps }
export default {}
</script>

<template>
  <OzTabsBase
    ref="baseComponent"
    :tabs="displayedTabs"
    :tab-list-aria-label="tabListAriaLabel"
    :current-tab-key="currentTabKey"
    :accessibility-role="accessibilityRole"
    :tabs-container-classes="[tabsContainerClasses, 'items-center overflow-x-auto no-scrollbar gap-[--tab-item-gap]']"
    :scrollable="false"
    :style="{ '--tab-item-gap': `${tabItemGap}px` }"
    @activate-tab="(event, tab) => $emit('activate-tab', event, tab)"
    @arrow-left="(targetTab) => handleKeyboardNavigation('left', targetTab)"
    @arrow-right="(targetTab) => handleKeyboardNavigation('right', targetTab)"
  >
    <template #tab="{ tab, isActive, tabAttributes, activateTab }">
      <!-- Navigation buttons for mouse users. They are hidden from screen readers -->
      <!-- and keyboard navigation to allow direct tab-to-tab navigation with arrow keys. -->
      <OzBaseButton
        v-if="tab.key === PREVIOUS_BUTTON_KEY || tab.key === NEXT_BUTTON_KEY"
        :tabindex="-1"
        :aria-hidden="true"
        :class="[
          'flex',
          'justify-center',
          'items-center',

          'outline-none',

          // Size
          sizePreset === OzTabsPaginatedSizePreset.H44px && ['w-11', 'h-11', 'rounded-2xl'],
          sizePreset === OzTabsPaginatedSizePreset.H40px && ['w-10', 'h-10', 'rounded-2xl'],
          sizePreset === OzTabsPaginatedSizePreset.H32px && ['w-8', 'h-8', 'rounded-xl'],
          sizePreset === OzTabsPaginatedSizePreset.H28px && ['w-7', 'h-7', 'rounded-[10px]'],
          sizePreset === OzTabsPaginatedSizePreset.H24px && ['w-6', 'h-6', 'rounded-lg'],
          sizePreset === OzTabsPaginatedSizePreset.H18px && ['w-4.5', 'h-4.5', 'rounded'],

          // Base
          'bg-transparent',
          {
            'text-dark-text-200': darkMode === false,
            'text-light-text-200': darkMode === true,
            'text-dark-text-200 dark:text-light-text-200': darkMode === 'auto',
          },
          // Hovered
          {
            'hover-hover:hover:text-grape-500': darkMode === false,
            'hover-hover:hover:text-canary-500': darkMode === true,
            'hover-hover:hover:text-grape-500 hover-hover:dark:hover:text-canary-500': darkMode === 'auto',
          },
        ]"
        :data-testid="`${tab.key}Button`"
        @click="handleNavigationButtonClick(tab)"
        @mouseenter="($event) => $emit('mouseenter', $event, tab)"
        @mouseleave="($event) => $emit('mouseleave', $event, tab)"
      >
        <OzIcon :name="tab.iconName" :size="tab.iconSize" />
      </OzBaseButton>

      <!-- Filler tabs (used to maintain equal width between pages) -->
      <span
        v-else-if="tab.key.startsWith(FILLER_TAB_KEY_PREFIX)"
        :class="[
          'flex',
          sizePreset === OzTabsPaginatedSizePreset.H44px && ['w-11', 'h-11'],
          sizePreset === OzTabsPaginatedSizePreset.H40px && ['w-10', 'h-10'],
          sizePreset === OzTabsPaginatedSizePreset.H32px && ['w-8', 'h-8'],
          sizePreset === OzTabsPaginatedSizePreset.H28px && ['w-7', 'h-7'],
          sizePreset === OzTabsPaginatedSizePreset.H24px && ['w-6', 'h-6'],
          sizePreset === OzTabsPaginatedSizePreset.H18px && ['w-4.5', 'h-4.5'],
        ]"
      />

      <!-- Actual tab buttons -->
      <OzPlainButton
        v-else
        :class="[
          'flex',
          'justify-center',

          'outline-none',
          'rounded-xl',
          // Size
          'w-full',
          // Spacing
          'px-2 pt-2 pb-2.25',
          // Base
          isActive
            ? {
                'text-grape-500': darkMode === false,
                'text-canary-500': darkMode === true,
                'text-grape-500 dark:text-canary-500': darkMode === 'auto',
              }
            : {
                'text-dark-text-200': darkMode === false,
                'text-light-text-200': darkMode === true,
                'text-dark-text-200 dark:text-light-text-200': darkMode === 'auto',
              },
          // Hovered
          !tab.disabled && {
            'hover-hover:hover:text-grape-500': darkMode === false,
            'hover-hover:hover:text-canary-500': darkMode === true,
            'hover-hover:hover:text-grape-500 hover-hover:dark:hover:text-canary-500': darkMode === 'auto',
          },
          // Focused
          'ring-inset',
          'focus-visible:ring-2',
          {
            'focus-visible:ring-grape-500': darkMode === false,
            'focus-visible:ring-canary-500': darkMode === true,
            'focus-visible:ring-grape-500 dark:focus-visible:ring-canary-500': darkMode === 'auto',
          },
        ]"
        v-bind="computeTabAttributes(tab, tabAttributes)"
        :href="tab.href"
        :color-scheme="OzPlainButtonColorScheme.Bare"
        :size-preset="sizePreset"
        :dark-mode="darkMode"
        :aria-label="tab.ariaLabel"
        :title="tab.title"
        :disabled="tab.disabled"
        :data-testid="`${tab.key}Button`"
        @click="activateTab"
        @mouseenter="($event) => $emit('mouseenter', $event, tab)"
        @mouseleave="($event) => $emit('mouseleave', $event, tab)"
        @focus="($event) => $emit('focus', $event, tab)"
        @blur="($event) => $emit('blur', $event, tab)"
      >
        <OzIcon :name="isActive && tab.activeIconName ? tab.activeIconName : tab.iconName" :size="tab.iconSize" />
      </OzPlainButton>
    </template>
  </OzTabsBase>
</template>
